import { api } from './apiClient';

export const onedriveService = {
  async getAuthUrl(): Promise<{ url: string }> {
    return api.get('/api/v1/onedrive/auth-url');
  },

  async getStatus(): Promise<{ connected: boolean; expires_at: string | null }> {
    return api.get('/api/v1/onedrive/status');
  },

  async disconnect(): Promise<{ success: boolean }> {
    return api.post('/api/v1/onedrive/disconnect');
  },

  async listImages(params?: {
    path?: string;
    limit?: number;
    includeDownloadUrl?: boolean;
  }): Promise<{
    items: {
      id: string;
      name: string;
      size: number;
      mimeType: string | null;
      webUrl: string | null;
      createdDateTime: string | null;
      lastModifiedDateTime: string | null;
      downloadUrl: string | null;
      already_imported?: boolean;
    }[];
    nextLink: string | null;
  }> {
    return api.get('/api/v1/onedrive/images', params as Record<string, string | number | boolean>);
  },

  async listChildren(params?: {
    parentId?: string;
    limit?: number;
    foldersOnly?: boolean;
    imagesOnly?: boolean;
    nextLink?: string;
    includeDownloadUrl?: boolean;
  }): Promise<{
    folders: { id: string; name: string; childCount: number | null }[];
    images: {
      id: string;
      name: string;
      size: number;
      mimeType: string | null;
      webUrl: string | null;
      createdDateTime: string | null;
      lastModifiedDateTime: string | null;
      downloadUrl: string | null;
      already_imported?: boolean;
    }[];
    nextLink: string | null;
  }> {
    return api.get('/api/v1/onedrive/children', params as Record<string, string | number | boolean>);
  },

  async searchImages(params: {
    query: string;
    limit?: number;
  }): Promise<{
    images: {
      id: string;
      name: string;
      size: number;
      mimeType: string | null;
      webUrl: string | null;
      createdDateTime: string | null;
      lastModifiedDateTime: string | null;
      already_imported?: boolean;
    }[];
  }> {
    return api.get('/api/v1/onedrive/search', params as Record<string, string | number | boolean>);
  },

  // Enqueue a background import job. Returns immediately with a jobId; poll
  // getImportStatus to track progress. Avoids long-running requests that hit
  // proxy/gateway timeouts on large (10k+) imports.
  async startImport(payload: {
    brand: string;
    product_id?: number;
    items: {
      id: string;
      name: string;
      mimeType?: string | null;
      size?: number;
      downloadUrl?: string | null;
      matched_sku?: string;
      sku_confidence?: number;
      original_filename?: string;
    }[];
  }): Promise<{ jobId: string; total: number }> {
    return api.post('/api/v1/onedrive/import', payload);
  },

  async getImportStatus(jobId: string): Promise<{
    jobId: string;
    status: 'running' | 'done' | 'error';
    total: number;
    processed: number;
    success: number;
    errors: number;
    skipped: number;
    error: string | null;
    results?: {
      success: boolean;
      skipped?: boolean;
      originalFilename: string;
      finalFilename: string;
      matchedSku?: string;
      confidence?: number;
      webpUrl?: string;
      error?: string;
    }[];
  }> {
    return api.get('/api/v1/onedrive/import/status', { jobId });
  },

  // Convenience wrapper for small/synchronous imports (e.g. single-product
  // match-missing). Enqueues a job and polls to completion, returning the same
  // {results, summary} shape callers previously relied on.
  async importImages(payload: {
    brand: string;
    product_id?: number;
    items: {
      id: string;
      name: string;
      mimeType?: string | null;
      downloadUrl?: string | null;
      matched_sku?: string;
      sku_confidence?: number;
      original_filename?: string;
    }[];
  }): Promise<{
    results: {
      success: boolean;
      skipped?: boolean;
      originalFilename: string;
      finalFilename: string;
      matchedSku?: string;
      confidence?: number;
      webpUrl?: string;
      error?: string;
    }[];
    summary: { total: number; success: number; errors: number; skipped: number };
  }> {
    const { jobId } = await this.startImport(payload);
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((r) => setTimeout(r, 1500));
      const s = await this.getImportStatus(jobId);
      if (s.status !== 'running') {
        if (s.status === 'error') throw new Error(s.error || 'Import failed');
        return {
          results: s.results ?? [],
          summary: { total: s.total, success: s.success, errors: s.errors, skipped: s.skipped },
        };
      }
    }
  },

  async matchMissing(params?: { limit?: number; offset?: number; brand?: string }): Promise<{
    data: {
      product: {
        id: number;
        sku: string | null;
        name: string;
        brand: string;
        image_url: string | null;
      };
      matches: {
        id: string;
        name: string;
        size: number;
        mimeType: string | null;
        webUrl: string | null;
        createdDateTime: string | null;
        lastModifiedDateTime: string | null;
      }[];
      reason?: string;
    }[];
  }> {
    return api.get('/api/v1/onedrive/match-missing', params as Record<string, string | number | boolean>);
  },
};
